import { DOCUMENT } from '@angular/common';
import { afterNextRender, Component, DestroyRef, effect, inject, NgZone, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { LangPipe } from '@reforgium/presentia';

import { AccessibilityService } from './accessibility.service';
import { AccessibilitySpeechService } from './accessibility-speech.service';
import { isReadableElement, readControlName, readSelectedText, readVisibleText } from './readable-text';

interface ReadableText {
  readonly element: Element;
  readonly text: string;
  readonly language?: string;
  readonly left: number;
  readonly top: number;
  readonly selected: boolean;
}

const TEXT_BLOCKS = 'p, h1, h2, h3, h4, h5, h6, li, td, th, blockquote, figcaption, pre, dt, dd';
const CONTROLS = 'a[href], button, [role="button"], [role="link"]';
const ASSISTANT_UI =
  'app-accessibility-hover-speech, .hover-speech, .speech-picker-trigger, .speech-picker-help, app-accessibility-panel';

@Component({
  selector: 'app-accessibility-hover-speech',
  imports: [LangPipe],
  template: `
    @if (hoveredText(); as item) {
      <div
        class="hover-speech"
        [class.hover-speech--selected]="item.selected || speech.state() === 'error'"
        [style.left.px]="item.left"
        [style.top.px]="item.top"
        role="group"
        [attr.aria-label]="'common.accessibility.readText' | lang"
        (pointerenter)="cancelHide()"
        (pointerleave)="scheduleHide()"
        (focusin)="cancelHide()"
        (focusout)="scheduleHide()"
      >
        @if (item.selected) {
          <p class="hover-speech__excerpt">{{ item.text.length > 160 ? item.text.slice(0, 160) + '…' : item.text }}</p>
        }
        <button
          type="button"
          class="hover-speech__play"
          [attr.aria-disabled]="speech.state() === 'speaking' && playingText() === item.text"
          (click)="play(item)"
        >
          <span aria-hidden="true">▶</span>
          {{ 'common.accessibility.play' | lang }}
        </button>
        <button
          class="hover-speech__secondary"
          type="button"
          [attr.aria-disabled]="speech.state() !== 'speaking'"
          [attr.aria-label]="'common.accessibility.stop' | lang"
          (click)="stop()"
        >
          <span aria-hidden="true">■</span>
        </button>
        <button
          class="hover-speech__secondary"
          type="button"
          [attr.aria-label]="'common.accessibility.closeSpeech' | lang"
          (click)="dismiss()"
        >
          <span aria-hidden="true">×</span>
        </button>
        @if (speech.state() === 'error') {
          <p class="hover-speech__status" role="status">{{ 'common.accessibility.soundError' | lang }}</p>
        }
      </div>
    }
    @if (available()) {
      @if (selecting()) {
        <p id="speech-picker-instructions" class="speech-picker-help" role="status">
          {{ (missingName() ? 'common.accessibility.pickMissingName' : 'common.accessibility.pickInstruction') | lang }}
        </p>
      }
      <button
        type="button"
        class="speech-picker-trigger"
        [attr.aria-pressed]="selecting()"
        [attr.aria-describedby]="selecting() ? 'speech-picker-instructions' : null"
        (click)="togglePicking()"
      >
        {{ (selecting() ? 'common.accessibility.cancelPicking' : 'common.accessibility.pickElement') | lang }}
      </button>
    }
  `,
  styles: `
    :host {
      display: contents;
    }
    .hover-speech {
      position: fixed;
      z-index: 1101;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.35rem;
      box-sizing: border-box;
      width: min(22rem, calc(100vw - 16px));
      padding: 0.4rem;
      border: 1px solid var(--border-color, #d6e2ec);
      border-radius: 0.8rem;
      background: var(--surface-neutral, #fff);
      box-shadow: 0 0.6rem 1.6rem rgb(17 40 75 / 22%);
      color: var(--text-primary, #172b45);
    }
    .hover-speech button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.45rem;
      min-width: 44px;
      min-height: 44px;
      padding: 0.45rem;
      border: 1px solid var(--border-color, #d6e2ec);
      border-radius: 0.55rem;
      background: var(--surface-background, #f1f5f9);
      color: var(--text-primary, #172b45);
      font: inherit;
      font-size: 0.85rem;
      font-weight: 650;
      cursor: pointer;
    }
    .hover-speech__play {
      flex: 1;
      min-width: 0 !important;
      overflow-wrap: anywhere;
    }
    .hover-speech button:not([aria-disabled='true']):hover {
      border-color: currentColor;
    }
    .hover-speech button:focus-visible {
      outline: 3px solid var(--text-primary, #172b45);
      outline-offset: 2px;
    }
    .hover-speech button[aria-disabled='true'] {
      opacity: 0.5;
      cursor: default;
    }
    .hover-speech__excerpt {
      flex: 0 0 100%;
      margin: 0.15rem 0 0.35rem;
      font-size: 0.85rem;
      overflow-wrap: anywhere;
    }
    .hover-speech__status {
      flex: 0 0 100%;
      margin: 0.3rem 0;
      font-size: 0.85rem;
      overflow-wrap: anywhere;
    }
    .hover-speech__secondary {
      flex: 0 0 44px;
    }
    .hover-speech--selected {
      inset: auto 16px calc(72px + env(safe-area-inset-bottom)) auto !important;
    }
    @media (max-width: 768px), (hover: none) {
      .hover-speech {
        inset: auto 8px calc(72px + env(safe-area-inset-bottom)) 8px !important;
        width: auto;
      }
    }

    .speech-picker-trigger,
    .speech-picker-help {
      position: fixed;
      z-index: 1101;
      box-sizing: border-box;
      right: max(12px, env(safe-area-inset-right));
      border: 1px solid var(--border-color, #d6e2ec);
      border-radius: 0.8rem;
      background: var(--surface-neutral, #fff);
      color: var(--text-primary, #172b45);
      font: inherit;
      font-size: 0.85rem;
      overflow-wrap: anywhere;
      box-shadow: 0 0.6rem 1.6rem rgb(17 40 75 / 22%);
    }
    .speech-picker-trigger {
      bottom: calc(12px + env(safe-area-inset-bottom));
      max-width: calc(100vw - 80px);
      min-height: 44px;
      padding: 0.55rem 0.75rem;
      cursor: pointer;
    }
    .speech-picker-trigger:focus-visible {
      outline: 3px solid var(--text-primary, #172b45);
      outline-offset: 2px;
    }
    .speech-picker-trigger[aria-pressed='true'] {
      border-width: 2px;
    }
    .speech-picker-help {
      bottom: calc(12px + 5.5rem + env(safe-area-inset-bottom));
      width: min(22rem, calc(100vw - 24px));
      margin: 0;
      padding: 0.75rem;
    }
    @media (max-width: 768px), (hover: none) {
      .hover-speech,
      .hover-speech--selected {
        bottom: calc(12px + 5.5rem + env(safe-area-inset-bottom)) !important;
      }
    }
    @media print {
      .hover-speech,
      .speech-picker-trigger,
      .speech-picker-help {
        display: none;
      }
    }
  `,
})
export class AccessibilityHoverSpeechComponent {
  protected readonly speech = inject(AccessibilitySpeechService);
  protected readonly playingText = signal('');
  protected readonly selecting = signal(false);
  protected readonly missingName = signal(false);
  private readonly guestRoute = signal(false);
  protected readonly hoveredText = signal<ReadableText | null>(null);

  private readonly document = inject(DOCUMENT);
  private readonly accessibility = inject(AccessibilityService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly frames = new Map<HTMLIFrameElement, () => void>();
  private observer?: MutationObserver;
  private hideTimer?: number;
  private dismissedElement: Element | null = null;
  private dismissedSelection = '';
  private returnFocus: HTMLElement | null = null;

  constructor() {
    const routeSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.guestRoute.set(this.isGuestRoute());
        this.clear();
        this.syncFrames();
      }
    });

    effect(() => {
      if (!this.accessibility.settings().soundAssistant) this.clear();
    });

    let detachDocument = () => {};

    afterNextRender(() => {
      this.guestRoute.set(this.isGuestRoute());
      detachDocument = this.listen(this.document);
      this.zone.runOutsideAngular(() => {
        this.observer = new MutationObserver(() => this.syncFrames());
        this.observer.observe(this.document.body, { childList: true, subtree: true });
        this.syncFrames();
      });
    });

    this.destroyRef.onDestroy(() => {
      detachDocument();
      this.observer?.disconnect();
      for (const cleanup of this.frames.values()) cleanup();
      this.frames.clear();
      this.cancelHide();
      routeSubscription.unsubscribe();
    });
  }

  protected available(): boolean {
    return this.speech.supported && this.accessibility.settings().soundAssistant && this.guestRoute();
  }

  protected play(item: ReadableText): void {
    if (this.speech.state() === 'speaking' && this.playingText() === item.text) return;
    this.playingText.set(item.text);
    this.speech.playText(item.text, item.language);
  }

  protected stop(): void {
    if (this.speech.state() === 'speaking') this.speech.stop();
  }

  protected togglePicking(): void {
    const next = !this.selecting();

    this.dismiss();
    this.selecting.set(next);
  }

  protected dismiss(): void {
    this.selecting.set(false);
    this.missingName.set(false);
    const item = this.hoveredText();
    const focused = this.document.activeElement?.closest('.hover-speech');

    this.dismissedElement = item?.element ?? null;
    this.dismissedSelection = item?.selected ? item.text : '';
    this.cancelHide();
    this.speech.stop();
    if (focused && this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
    this.hoveredText.set(null);
  }

  protected cancelHide(): void {
    if (this.hideTimer !== undefined) window.clearTimeout(this.hideTimer);
    this.hideTimer = undefined;
  }

  protected scheduleHide(): void {
    this.cancelHide();
    this.hideTimer = window.setTimeout(() => {
      const item = this.hoveredText();

      if (!item || item.selected || this.speech.state() === 'speaking') return;
      if (this.document.activeElement?.closest('.hover-speech')) return;
      if (item.element.contains(item.element.ownerDocument.activeElement)) return;
      this.hoveredText.set(null);
    }, 250);
  }

  private clear(): void {
    this.selecting.set(false);
    this.missingName.set(false);
    this.cancelHide();
    this.hoveredText.set(null);
    this.dismissedElement = null;
    this.dismissedSelection = '';
    this.returnFocus = null;
  }

  private onTextOver(event: PointerEvent | FocusEvent, frame?: HTMLIFrameElement): void {
    if ('pointerType' in event && event.pointerType === 'touch') return;
    const target = event.target;

    if (!target || (target as Node).nodeType !== Node.ELEMENT_NODE) return;
    const element = target as Element;

    if (element.closest('.hover-speech')) {
      this.cancelHide();

      return;
    }

    if (!this.available() || this.selecting() || element.closest(ASSISTANT_UI)) return;
    if (!frame && !element.closest('header, main, footer, [role="main"]')) return;
    // Preserve a chosen or currently playing fragment while the pointer moves.
    if (this.hoveredText()?.selected || (this.hoveredText() && this.speech.state() === 'speaking')) return;

    const control = element.closest(CONTROLS);
    const block = control ?? element.closest(TEXT_BLOCKS) ?? this.nearestDirectText(element);

    if (!block || !isReadableElement(block, undefined, Boolean(control)) || block === this.dismissedElement) return;
    const text = control ? readControlName(control) : readVisibleText(block);

    if (!text) return;
    const rect = element.getBoundingClientRect();
    const x = 'clientX' in event ? event.clientX : rect.left;
    const y = 'clientY' in event ? event.clientY : rect.bottom;

    if (event.type === 'focusin') this.returnFocus = element as HTMLElement;
    this.show(block, text, x, y, false, frame);
  }

  private show(block: Element, text: string, x: number, y: number, selected: boolean, frame?: HTMLIFrameElement): void {
    this.cancelHide();
    const current = this.hoveredText();

    if (current?.element === block && current.text === text && current.selected === selected) return;
    const frameRect = frame?.getBoundingClientRect();
    const width = this.document.defaultView?.innerWidth ?? 1024;
    const height = this.document.defaultView?.innerHeight ?? 768;
    const rem = Number.parseFloat(
      this.document.defaultView?.getComputedStyle(this.document.documentElement).fontSize ?? '16',
    );

    this.hoveredText.set({
      element: block,
      text,
      language:
        block.closest('[lang]')?.getAttribute('lang') ??
        frame?.closest('[lang]')?.getAttribute('lang') ??
        block.ownerDocument.documentElement.lang,
      left: Math.max(8, Math.min(x + (frameRect?.left ?? 0) + 12, width - 22 * rem - 8)),
      top: Math.max(8, Math.min(y + (frameRect?.top ?? 0) + 16, height - Math.max(64, 4 * rem) - 8)),
      selected,
    });
  }

  private onPickClick(event: MouseEvent, frame?: HTMLIFrameElement): void {
    if (!this.selecting() || !this.available()) return;
    const target = event.target as Element | null;

    if (!target || target.nodeType !== Node.ELEMENT_NODE || target.closest(ASSISTANT_UI)) return;
    if (!frame && !target.closest('header, main, footer, [role="main"]')) return;
    const control = target.closest(CONTROLS);
    const block = control ?? target.closest(TEXT_BLOCKS) ?? this.nearestDirectText(target);

    if (!block || !isReadableElement(block, undefined, Boolean(control))) return;
    const text = control ? readControlName(control) : readVisibleText(block);

    // Selection consumes one activation only; scrolling and ordinary navigation remain intact.
    event.preventDefault();
    event.stopImmediatePropagation();

    if (!text) {
      this.missingName.set(true);

      return;
    }

    this.missingName.set(false);
    this.selecting.set(false);
    this.returnFocus = block.ownerDocument.activeElement as HTMLElement | null;
    this.show(block, text, event.clientX, event.clientY, true, frame);
    const item = this.hoveredText();

    if (item) this.play(item);
  }

  private onSelection(owner: Document, frame?: HTMLIFrameElement): void {
    if (!this.available() || this.selecting()) return;
    const root = frame ? owner.body : owner.querySelector('main, [role="main"]');
    const text = root ? readSelectedText(owner, root) : '';

    if (!text) {
      this.dismissedSelection = '';
      // Keep the captured fragment available when tapping the playback controls collapses the selection.

      return;
    }

    if (text === this.dismissedSelection || !root) return;

    if (!this.document.activeElement?.closest('.hover-speech')) {
      this.returnFocus = owner.activeElement as HTMLElement | null;
    }

    this.show(root, text, 0, 0, true, frame);
  }

  private onTextOut(event: PointerEvent | FocusEvent): void {
    const related = event.relatedTarget as Node | null;
    const item = this.hoveredText();

    if (related?.nodeType === Node.ELEMENT_NODE && (related as Element).closest('.hover-speech')) return;
    if (item && related?.nodeType && item.element.contains(related)) return;
    if (related?.nodeType && this.dismissedElement?.contains(related)) return;
    this.dismissedElement = null;
    if (item) this.scheduleHide();
  }

  private listen(owner: Document, frame?: HTMLIFrameElement): () => void {
    const onOver = (event: PointerEvent) => this.zone.run(() => this.onTextOver(event, frame));
    const onOut = (event: PointerEvent | FocusEvent) => this.zone.run(() => this.onTextOut(event));
    const onFocus = (event: FocusEvent) => this.zone.run(() => this.onTextOver(event, frame));
    const onSelection = () => this.zone.run(() => this.onSelection(owner, frame));

    const onClick = (event: MouseEvent) => this.zone.run(() => this.onPickClick(event, frame));

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && this.available()) this.zone.run(() => this.dismiss());
    };

    owner.addEventListener('click', onClick, true);
    owner.addEventListener('pointerover', onOver);
    owner.addEventListener('pointerout', onOut);
    owner.addEventListener('focusin', onFocus);
    owner.addEventListener('focusout', onOut);
    owner.addEventListener('selectionchange', onSelection);
    owner.addEventListener('keydown', onKey);

    return () => {
      owner.removeEventListener('click', onClick, true);
      owner.removeEventListener('pointerover', onOver);
      owner.removeEventListener('pointerout', onOut);
      owner.removeEventListener('focusin', onFocus);
      owner.removeEventListener('focusout', onOut);
      owner.removeEventListener('selectionchange', onSelection);
      owner.removeEventListener('keydown', onKey);
    };
  }

  private isGuestRoute(): boolean {
    let route = this.router.routerState.snapshot.root;

    while (route.firstChild) route = route.firstChild;

    return route.data['accessibilitySpeech'] === true;
  }

  private nearestDirectText(start: Element): Element | null {
    let element: Element | null = start;

    while (element && !element.matches('header, main, footer, [role="main"], body')) {
      if (
        Array.from(element.childNodes).some(
          (node) => node.nodeType === Node.TEXT_NODE && Boolean(node.textContent?.trim()),
        )
      )
        return element;
      element = element.parentElement;
    }

    return null;
  }

  private syncFrames(): void {
    const frames = new Set(
      this.isGuestRoute() ? this.document.querySelectorAll<HTMLIFrameElement>('app-redactor[readonly] iframe') : [],
    );

    for (const [frame, cleanup] of this.frames) {
      if (!frames.has(frame)) {
        cleanup();
        this.frames.delete(frame);
      }
    }

    for (const frame of frames) {
      if (this.frames.has(frame)) continue;

      let detachDocument = () => {};

      const attach = () => {
        detachDocument();

        try {
          if (frame.contentDocument) detachDocument = this.listen(frame.contentDocument, frame);
        } catch {
          // Browser-managed and cross-origin frames do not expose their document.
        }
      };

      frame.addEventListener('load', attach);
      attach();
      this.frames.set(frame, () => {
        frame.removeEventListener('load', attach);
        detachDocument();
      });
    }
  }
}
